"use client";

import {
  registerPlugin,
  type Plugin,
  type PluginListenerHandle,
} from "@capacitor/core";
import type { WebPlayerTrackDraft } from "@/hooks/webPlayerTypes";

export type AppleMusicNativeAuthorizeOptions = {
  developerToken: string;
  loginNonce?: string;
  ignoreCache?: boolean;
};

export type AppleMusicNativePlaybackTrack = WebPlayerTrackDraft;

export type AppleMusicNativePlaybackState = {
  playbackStatus?: string | null;
  isPreparedToPlay?: boolean | null;
  playbackTime?: number | null;
  positionMs?: number | null;
  durationMs?: number | null;
  queuePosition?: number | null;
  queueCount?: number | null;
  currentTrack?: AppleMusicNativePlaybackTrack | null;
};

export type AppleMusicNativePlayTrackOptions = {
  uri: string;
};

export type AppleMusicNativePlayTrackQueueOptions = {
  uris: string[];
  index?: number;
};

export type AppleMusicNativeSeekOptions = {
  seconds: number;
};

export type AppleMusicNativeAuthorizeResult = {
  authorizationStatus?: string;
  canPlayCatalogContent?: boolean;
  loginNonce?: string | null;
  musicUserToken?: string | null;
  storefront?: string | null;
  tokenIssuedAt?: number | null;
};

export type AppleMusicNativeDebugEvent = {
  operation?: string | null;
  stage?: string | null;
  status?: string | null;
  at?: string | null;
  details?: Record<string, unknown> | null;
  error?:
    | {
        message?: string | null;
        domain?: string | null;
        code?: string | number | null;
      }
    | null;
};

export interface AppleMusicNativeAuthPlugin extends Plugin {
  addListener(
    eventName: "nativePlaybackDebug",
    listenerFunc: (event: AppleMusicNativeDebugEvent) => void,
  ): Promise<PluginListenerHandle>;
  authorize(
    options: AppleMusicNativeAuthorizeOptions
  ): Promise<AppleMusicNativeAuthorizeResult>;
  getPlaybackState(): Promise<AppleMusicNativePlaybackState>;
  playTrack(
    options: AppleMusicNativePlayTrackOptions
  ): Promise<AppleMusicNativePlaybackState>;
  playTrackQueue(
    options: AppleMusicNativePlayTrackQueueOptions
  ): Promise<AppleMusicNativePlaybackState>;
  togglePlayback(): Promise<AppleMusicNativePlaybackState>;
  pausePlayback(): Promise<AppleMusicNativePlaybackState>;
  resumePlayback(): Promise<AppleMusicNativePlaybackState>;
  stopPlayback(): Promise<AppleMusicNativePlaybackState>;
  seekToTime(
    options: AppleMusicNativeSeekOptions
  ): Promise<AppleMusicNativePlaybackState>;
  skipToNextEntry(): Promise<AppleMusicNativePlaybackState>;
  skipToPreviousEntry(): Promise<AppleMusicNativePlaybackState>;
}

export const AppleMusicNativeAuth =
  registerPlugin<AppleMusicNativeAuthPlugin>("AppleMusicNativeAuth");
